/**
 * Schemas Zod para criação e atualização de Propostas.
 *
 * Esses são DTOs (Data Transfer Objects) — não incluem campos gerados
 * pelo servidor (id, numero, dataCriacao, pricing, _v, _ts).
 */

import { z } from 'zod';

import { clienteSchema, vendedorSchema } from './cliente.js';
import { textoLongoSchema } from './common.js';
import { contratoRecorrentePayloadSchema } from './payload-contrato-recorrente.js';
import {
  projetoPayloadSchema,
  vendaMaterialPayloadSchema,
  vendaServicoPayloadSchema,
} from './payload-projeto-venda.js';
import { renovacaoRecorrentePayloadSchema } from './payload-renovacao-recorrente.js';

// ── Tipo + Status ──────────────────────────────────────────────────

export const tipoPropostaSchema = z.enum([
  'contrato-recorrente',
  'renovacao-recorrente',
  'projeto-infra',
  'projeto-cftv',
  'projeto-controle-acesso',
  'venda-pecas',
  'venda-computador',
  'venda-distribuidor',
  'venda-projeto-servico',
]);

export const statusPropostaSchema = z.enum([
  'rascunho',
  'enviada',
  'visualizada',
  'aceita',
  'aguardando-dados-contrato',
  'contrato-gerado',
  'aguardando-assinatura',
  'assinada',
  'implantacao',
  'concluida',
  'perdida',
  'cancelada',
]);

// ── Criação ─────────────────────────────────────────────────────────

// Schema das opções de exibição na folha de apresentação
export const exibicaoConfigSchema = z.object({
  mostrarValoresUnitarios: z.boolean(),
  mostrarSubtotais: z.boolean(),
  mostrarItensDetalhados: z.boolean(),
  /**
   * Modo de apresentação dos add-ons na view pública.
   * - 'compacto': linha a linha com qtd e valor (default — proposta master).
   * - 'completo': card por produto com descrição/funcionalidades + preço.
   * Útil quando o contrato é só add-on (Backup, Mikrotik, M365, etc).
   */
  modoAddOns: z.enum(['compacto', 'completo']),
});

// Campos comuns aos 4 tipos de criação
const camposComuns = {
  cliente: clienteSchema,
  vendedor: vendedorSchema,
  validade: z.string().min(1).max(50),
  observacoes: textoLongoSchema.optional(),
  /** Observações internas (#237) — só equipe Infomatic vê. */
  observacoesInternas: textoLongoSchema.optional(),
  exibicao: exibicaoConfigSchema.partial().optional(),
};

export const criarPropostaContratoRecorrenteSchema = z.object({
  ...camposComuns,
  tipo: z.literal('contrato-recorrente'),
  payload: contratoRecorrentePayloadSchema,
});

export const criarPropostaRenovacaoRecorrenteSchema = z.object({
  ...camposComuns,
  tipo: z.literal('renovacao-recorrente'),
  payload: renovacaoRecorrentePayloadSchema,
});

export const criarPropostaProjetoSchema = z.object({
  ...camposComuns,
  tipo: z.enum(['projeto-infra', 'projeto-cftv', 'projeto-controle-acesso']),
  payload: projetoPayloadSchema,
});

export const criarPropostaVendaMaterialSchema = z.object({
  ...camposComuns,
  tipo: z.enum(['venda-pecas', 'venda-computador', 'venda-distribuidor']),
  payload: vendaMaterialPayloadSchema,
});

export const criarPropostaVendaServicoSchema = z.object({
  ...camposComuns,
  tipo: z.literal('venda-projeto-servico'),
  payload: vendaServicoPayloadSchema,
});

/** Union discriminada dos 5 tipos de criação (inclui renovacao-recorrente) */
export const criarPropostaSchema = z.discriminatedUnion('tipo', [
  criarPropostaContratoRecorrenteSchema,
  criarPropostaRenovacaoRecorrenteSchema,
  criarPropostaProjetoSchema,
  criarPropostaVendaMaterialSchema,
  criarPropostaVendaServicoSchema,
]);

export type CriarPropostaInput = z.infer<typeof criarPropostaSchema>;

// ── Atualização (PATCH parcial) ────────────────────────────────────

/**
 * Patch parcial de uma proposta. Só rascunho aceita patch.
 * Campos imutáveis (id, numero, tipo, dataCriacao, _v) ficam de fora.
 *
 * O `payload` aceita os campos de contrato-recorrente OU renovacao-recorrente,
 * tudo opcional. O backend valida o subconjunto correto baseado no `tipo`
 * já gravado da proposta. Renovação tem campos extras (contratoOrigem,
 * reajuste, novaVigencia) que não podem ser patcheados em contrato-recorrente.
 *
 * Bug #254: antes do merge abaixo, o schema usava só renovacaoRecorrentePayloadSchema.
 * O strip do zod removia silenciosamente pacotePrincipal/recursos/etc do PATCH
 * em propostas contrato-recorrente, fazendo qualquer edição preservar o pacote
 * antigo. Sintoma: "escolho essencial, salva como professional".
 */
const payloadPatchSchema = contratoRecorrentePayloadSchema
  .partial()
  .merge(renovacaoRecorrentePayloadSchema.partial());

export const atualizarPropostaSchema = z.object({
  cliente: clienteSchema.partial().optional(),
  validade: z.string().min(1).max(50).optional(),
  observacoes: textoLongoSchema.optional(),
  observacoesInternas: textoLongoSchema.optional(),
  payload: payloadPatchSchema.optional(),
  exibicao: exibicaoConfigSchema.partial().optional(),
});

export type AtualizarPropostaInput = z.infer<typeof atualizarPropostaSchema>;

// ── Mudança de status ──────────────────────────────────────────────

export const mudarStatusSchema = z.object({
  novoStatus: statusPropostaSchema,
  motivo: textoLongoSchema.optional(),
});

export type MudarStatusInput = z.infer<typeof mudarStatusSchema>;

// ── Filtros de listagem ────────────────────────────────────────────

export const listarPropostasFiltrosSchema = z.object({
  status: statusPropostaSchema.optional(),
  tipo: tipoPropostaSchema.optional(),
  vendedor: z.string().email().optional(),
  empresa: z.string().min(1).max(200).optional(),
  /** Continuation token do Cosmos pra paginação */
  continuation: z.string().optional(),
  /** Page size — default 50, max 200 */
  pageSize: z.number().int().min(1).max(200).optional(),
});

export type ListarPropostasFiltros = z.infer<typeof listarPropostasFiltrosSchema>;
